import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { formatDate, formatIDR } from "../lib/format";
import { Button } from "../components/ui/button";
import { Printer } from "lucide-react";

export default function PrintReceipt() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  useEffect(() => { api.get(`/receipts/${id}`).then(({ data }) => setR(data)); }, [id]);
  if (!r) return <div className="p-10">Memuat...</div>;
  return <PrintDoc doc={r} kind="receipt" />;
}

export const PrintDoc = ({ doc, kind }) => {
  const titles = { receipt: "KWITANSI PEMBAYARAN", invoice: "INVOICE", quotation: "PENAWARAN", wage: "SLIP PEMBAYARAN FEE CREW" };
  return (
    <div className="min-h-screen bg-white p-6 print:p-10 max-w-3xl mx-auto text-sm">
      <div className="print:hidden mb-4 flex justify-end">
        <Button onClick={() => window.print()} className="bg-brand text-white" data-testid="btn-do-print">
          <Printer className="w-4 h-4 mr-1.5" /> Cetak
        </Button>
      </div>
      <div className="border-b-2 border-[#C87D55] pb-4 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <div className="font-display text-2xl font-bold">DESSY WEDDING GROUP</div>
            <div className="text-xs text-[#5C6066]">Wedding & Event Services • Garut, Jawa Barat</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-[#8C9096] font-semibold">{titles[kind]}</div>
            <div className="font-mono text-sm mt-1">{doc.receipt_code || doc.invoice_code || doc.quotation_code || doc.wage_payment_code}</div>
          </div>
        </div>
      </div>

      {kind === "receipt" && (
        <>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div><div className="text-[10px] uppercase text-[#8C9096]">Diterima dari</div><div className="font-medium">{doc.client_name}</div></div>
            <div><div className="text-[10px] uppercase text-[#8C9096]">Pemesanan</div><div className="font-mono text-xs">{doc.booking_code}</div></div>
            <div><div className="text-[10px] uppercase text-[#8C9096]">Tanggal Bayar</div><div>{formatDate(doc.payment_date)}</div></div>
            <div><div className="text-[10px] uppercase text-[#8C9096]">Metode</div><div>{doc.method}</div></div>
          </div>
          <div className="p-6 bg-[#FAF6EE] rounded border border-[#E5E0D8] mb-6">
            <div className="text-xs uppercase text-[#8C9096]">Jumlah diterima</div>
            <div className="font-display text-3xl font-bold mt-1">{formatIDR(doc.amount)}</div>
            <div className="text-sm text-[#5C6066] mt-2">Untuk pembayaran: {doc.payment_type}</div>
          </div>
          {doc.notes && <div className="mb-6 text-sm"><span className="text-[#8C9096]">Catatan: </span>{doc.notes}</div>}
        </>
      )}

      {kind === "invoice" && (
        <>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div><div className="text-[10px] uppercase text-[#8C9096]">Kepada</div><div className="font-medium">{doc.client?.bride_name} & {doc.client?.groom_name}</div><div className="text-xs">{doc.client?.address}</div></div>
            <div>
              <div><div className="text-[10px] uppercase text-[#8C9096]">Pemesanan</div><div className="font-mono text-xs">{doc.booking_code}</div></div>
              <div className="mt-2"><div className="text-[10px] uppercase text-[#8C9096]">Tgl Invoice / Jatuh Tempo</div><div>{formatDate(doc.invoice_date)} — {formatDate(doc.due_date)}</div></div>
            </div>
          </div>
          <table className="w-full border-t border-b border-[#E5E0D8] mb-6">
            <thead><tr className="bg-[#FAF6EE]"><th className="text-left p-3">Deskripsi</th><th className="text-right p-3">Nominal</th></tr></thead>
            <tbody><tr className="border-t border-[#F0EBE0]"><td className="p-3">{doc.payment_type}<div className="text-xs text-[#8C9096]">{doc.notes || `Untuk acara ${doc.booking?.event_name}`}</div></td><td className="p-3 text-right font-mono">{formatIDR(doc.amount)}</td></tr></tbody>
            <tfoot><tr className="border-t-2 border-[#C87D55]"><td className="p-3 font-semibold">Total</td><td className="p-3 text-right font-mono font-bold">{formatIDR(doc.amount)}</td></tr></tfoot>
          </table>
        </>
      )}

      {kind === "quotation" && (
        <>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div><div className="text-[10px] uppercase text-[#8C9096]">Untuk</div><div className="font-medium">{doc.client?.bride_name} & {doc.client?.groom_name}</div></div>
            <div><div className="text-[10px] uppercase text-[#8C9096]">Acara</div><div>{doc.event_name}</div><div className="text-xs">{formatDate(doc.event_date)} • {doc.venue}</div></div>
          </div>
          <table className="w-full border-t border-b border-[#E5E0D8] mb-4">
            <thead><tr className="bg-[#FAF6EE]"><th className="text-left p-3">Deskripsi</th><th className="text-right p-3">Qty</th><th className="text-right p-3">Harga</th><th className="text-right p-3">Subtotal</th></tr></thead>
            <tbody>{doc.lines?.map((l, i) => (
              <tr key={i} className="border-t border-[#F0EBE0]"><td className="p-3">{l.description}</td><td className="p-3 text-right">{l.quantity}</td><td className="p-3 text-right font-mono">{formatIDR(l.unit_price)}</td><td className="p-3 text-right font-mono">{formatIDR(l.quantity * l.unit_price)}</td></tr>
            ))}</tbody>
            <tfoot>
              <tr><td colSpan={3} className="p-2 text-right">Subtotal</td><td className="p-2 text-right font-mono">{formatIDR(doc.subtotal)}</td></tr>
              <tr><td colSpan={3} className="p-2 text-right">Diskon</td><td className="p-2 text-right font-mono">- {formatIDR(doc.discount)}</td></tr>
              <tr className="border-t-2 border-[#C87D55]"><td colSpan={3} className="p-3 text-right font-semibold">Total</td><td className="p-3 text-right font-mono font-bold">{formatIDR(doc.final_total)}</td></tr>
            </tfoot>
          </table>
          {doc.terms && <div className="text-xs mb-3"><div className="font-semibold mb-1">Syarat & Ketentuan</div><p className="whitespace-pre-wrap">{doc.terms}</p></div>}
          <div className="text-xs text-[#8C9096]">Berlaku sampai: {formatDate(doc.valid_until)}</div>
        </>
      )}

      {kind === "wage" && (
        <>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div><div className="text-[10px] uppercase text-[#8C9096]">Tanggal Pembayaran</div><div>{formatDate(doc.payment_date)}</div></div>
            <div><div className="text-[10px] uppercase text-[#8C9096]">Metode</div><div>{doc.method}</div></div>
          </div>
          <table className="w-full border-t border-b border-[#E5E0D8] mb-4">
            <thead><tr className="bg-[#FAF6EE]"><th className="text-left p-2">Nama</th><th className="text-left p-2">Role</th><th className="text-right p-2">Fee</th><th className="text-right p-2">Tambahan</th><th className="text-right p-2">Potongan</th><th className="text-right p-2">Akhir</th></tr></thead>
            <tbody>{doc.wages?.map((w) => (
              <tr key={w.id} className="border-t border-[#F0EBE0]"><td className="p-2">{w.staff_name}</td><td className="p-2">{w.role}</td><td className="p-2 text-right font-mono">{formatIDR(w.base_fee)}</td><td className="p-2 text-right font-mono">{formatIDR(w.additional_fee)}</td><td className="p-2 text-right font-mono">{formatIDR(w.deduction)}</td><td className="p-2 text-right font-mono font-semibold">{formatIDR(w.final_amount)}</td></tr>
            ))}</tbody>
            <tfoot><tr className="border-t-2 border-[#C87D55]"><td colSpan={5} className="p-3 text-right font-semibold">Total</td><td className="p-3 text-right font-mono font-bold">{formatIDR(doc.total_amount)}</td></tr></tfoot>
          </table>
        </>
      )}

      <div className="mt-16 grid grid-cols-2 gap-8">
        <div><div className="text-xs text-[#8C9096] mb-16">Dibuat oleh</div><div className="border-t border-[#5C6066] pt-2 text-sm">{doc.recorded_by || doc.created_by || "DWOS"}</div></div>
        <div><div className="text-xs text-[#8C9096] mb-16">Diterima oleh</div><div className="border-t border-[#5C6066] pt-2 text-sm">Tanda tangan & nama</div></div>
      </div>
      <div className="mt-8 text-center text-[10px] text-[#8C9096]">Dessy Wedding Group • Terima kasih atas kepercayaan Anda</div>
    </div>
  );
};
